<template>
  <div class="ship-monitor-container">
    <ship-map-view ref="shipMapView"></ship-map-view>
  </div>
</template>

<script>
import ShipMapView from '@/components/maritime/ShipMapView.vue'

export default {
  name: 'ShipMonitor',
  components: {
    ShipMapView
  },
  data() {
    return {
      lastRoutePath: null, // 记录上次路由路径
      analysisRefreshTimer: null // 分析面板自动刷新定时器
    };
  },
  created() {
    // 监听路由变化，以便在路由变化时检查是否需要显示告警面板
    this.$watch(
      () => this.$route,
      this.checkRoute,
      { immediate: true }
    );
    
    // 监听菜单点击事件，直接触发弹窗显示
    this.$root.$on('menu-clicked', this.handleMenuClick);
    // 监听新的弹窗触发事件
    this.$root.$on('menu-modal-trigger', this.handleModalTrigger);
    // 启动分析面板定时刷新
    this.startAnalysisRefreshTimer();
  },
  
  beforeDestroy() {
    // 移除事件监听
    this.$root.$off('menu-clicked', this.handleMenuClick);
    this.$root.$off('menu-modal-trigger', this.handleModalTrigger);
    this.clearAnalysisRefreshTimer();
  },
  methods: {
    /**
     * 启动分析面板定时刷新
     */
    startAnalysisRefreshTimer() {
      this.clearAnalysisRefreshTimer();
      this.analysisRefreshTimer = setInterval(() => {
        this.refreshAnalysisPanels();
      }, 30000);
    },

    /**
     * 清理分析面板定时刷新
     */
    clearAnalysisRefreshTimer() {
      if (this.analysisRefreshTimer) {
        clearInterval(this.analysisRefreshTimer);
        this.analysisRefreshTimer = null;
      }
    },

    /**
     * 递归查找指定名称的子组件实例
     */
    findChildComponentByName(rootVm, componentName) {
      if (!rootVm || !rootVm.$children || !rootVm.$children.length) return null;
      for (const child of rootVm.$children) {
        if (child && child.$options && child.$options.name === componentName) {
          return child;
        }
        const nested = this.findChildComponentByName(child, componentName);
        if (nested) return nested;
      }
      return null;
    },

    /**
     * 刷新所有已打开的分析类面板
     */
    refreshAnalysisPanels() {
      const shipMapView = this.$refs.shipMapView;
      if (!shipMapView) return;

      // 统计分析面板（偏航/流量/预警）
      if (shipMapView.deviationStatisticsVisible) {
        const statisticsPanel = this.findChildComponentByName(shipMapView, 'BridgeStatistics');
        if (statisticsPanel && typeof statisticsPanel.loadCurrentTabStatistics === 'function') {
          statisticsPanel.loadCurrentTabStatistics();
        }
      }

      // 通航记录面板
      if (shipMapView.trafficPanelVisible) {
        const trafficPanel = this.findChildComponentByName(shipMapView, 'BridgeNavigationPanel');
        if (trafficPanel && typeof trafficPanel.loadData === 'function') {
          trafficPanel.loadData();
        }
      }

      // VHF呼叫记录面板（统计+列表）
      if (shipMapView.vhfCallRecordVisible) {
        const vhfPanel = this.findChildComponentByName(shipMapView, 'VhfCallRecordPanel');
        if (vhfPanel) {
          if (typeof vhfPanel.loadStatistics === 'function') {
            vhfPanel.loadStatistics();
          }
          if (typeof vhfPanel.searchRecords === 'function') {
            vhfPanel.searchRecords();
          }
        }
      }

      // 预警规则面板
      if (shipMapView.bridgeAlertRulesVisible) {
        const bridgeRulesPanel = this.findChildComponentByName(shipMapView, 'BridgeAlertRules');
        if (bridgeRulesPanel && typeof bridgeRulesPanel.refreshRules === 'function') {
          bridgeRulesPanel.refreshRules();
        }
      }
    },

    /**
     * 处理菜单点击事件，直接显示对应弹窗
     */
    handleMenuClick(eventData) {
      console.log('菜单点击事件:', eventData);
      
      // 延迟执行，确保ShipMapView组件已完全加载
      this.$nextTick(() => {
        const shipMapView = this.$refs.shipMapView;
        if (!shipMapView) {
          console.warn('ShipMapView组件未找到');
          return;
        }
        
        // 根据菜单ID显示对应的弹窗
        switch (eventData.menuId) {
          case 'bridgeRecord':
            // 桥梁通航记录
            shipMapView.trafficPanelVisible = true;
            shipMapView.bringPanelToFront('traffic');
            break;
            
          case 'bridgeWarningAll':
            // 桥梁预警信息
            shipMapView.alertDetailVisible = true;
            shipMapView.alertActiveTab = 'records';
            shipMapView.bringPanelToFront('alert');
            break;
            
          case 'statisticsYaw':
            // 桥梁偏航统计
            shipMapView.deviationStatisticsVisible = true;
            shipMapView.statisticsActiveTab = 'deviation';
            shipMapView.bringPanelToFront('statistics');
            break;
            
          case 'statisticsBridgeReportRecord':
            // 流量统计
            shipMapView.deviationStatisticsVisible = true;
            shipMapView.statisticsActiveTab = 'traffic';
            shipMapView.bringPanelToFront('statistics');
            break;
            
          case 'statisticsBridge':
            // 桥梁预警统计
            shipMapView.deviationStatisticsVisible = true;
            shipMapView.statisticsActiveTab = 'alert';
            shipMapView.bringPanelToFront('statistics');
            break;
            
          case 'statisticsSuperelevation':
            // 桥梁超高统计 - 使用同一个统计面板
            shipMapView.deviationStatisticsVisible = true;
            shipMapView.statisticsActiveTab = 'superelevation';
            shipMapView.bringPanelToFront('statistics');
            break;
            
          case 'statisticsOverspeed':
            // 桥梁超速统计 - 使用同一个统计面板
            shipMapView.deviationStatisticsVisible = true;
            shipMapView.statisticsActiveTab = 'overspeed';
            shipMapView.bringPanelToFront('statistics');
            break;
            
          case 'statisticsBridgeForbidRecord':
            // 桥梁禁航统计 - 使用同一个统计面板
            shipMapView.deviationStatisticsVisible = true;
            shipMapView.statisticsActiveTab = 'forbid';
            shipMapView.bringPanelToFront('statistics');
            break;
            
          case 'statisticsVhf':
            // 桥梁VHF统计 - 使用同一个统计面板
            shipMapView.deviationStatisticsVisible = true;
            shipMapView.statisticsActiveTab = 'vhf';
            shipMapView.bringPanelToFront('statistics');
            break;
            
          case 'vhfPushAll':
            // VHF呼叫记录
            shipMapView.vhfCallRecordVisible = true;
            shipMapView.bringPanelToFront('vhf');
            break;
            
          case 'bridgeRuleAll':
            // 桥梁预警规则
            shipMapView.bridgeAlertRulesVisible = true;
            shipMapView.bridgeRulesActiveTab = 'rules';
            shipMapView.bringPanelToFront('bridgeRules');
            break;
            
          default:
            console.log('未处理的菜单项:', eventData.menuId);
        }
      });
    },
    
    /**
     * 处理弹窗触发事件
     */
    handleModalTrigger(eventData) {
      console.log('【ShipMonitor】弹窗触发事件:', eventData);
      
      // 延迟执行，确保ShipMapView组件已完全加载
      this.$nextTick(() => {
        const shipMapView = this.$refs.shipMapView;
        if (!shipMapView) {
          console.warn('ShipMapView组件未找到');
          return;
        }
        
        // 根据modalTrigger显示对应的弹窗
        switch (eventData.modalTrigger) {
          case 'bridgeTrafficRecord':
            // 桥梁通航记录
            console.log('【ShipMonitor】显示桥梁通航记录面板');
            shipMapView.trafficPanelVisible = true;
            shipMapView.bringPanelToFront('traffic');
            break;
            
          case 'bridgeAlerts':
            // 桥梁预警信息
            console.log('【ShipMonitor】显示桥梁预警信息面板');
            shipMapView.alertDetailVisible = true;
            shipMapView.alertActiveTab = 'records';
            shipMapView.bringPanelToFront('alert');
            break;
            
          case 'bridgeDeviationStatistics':
            // 统计分析面板
            console.log('【ShipMonitor】显示统计分析面板, 标签页:', eventData.modalTab);
            shipMapView.deviationStatisticsVisible = true;
            if (eventData.modalTab) {
              shipMapView.statisticsActiveTab = eventData.modalTab;
            }
            shipMapView.bringPanelToFront('statistics');
            break;
            
          default:
            console.log('未处理的弹窗触发器:', eventData.modalTrigger);
        }
      });
    },
    
    /**
     * 检查当前路由是否需要显示告警面板
     */
    checkRoute(route) {
      // 检查路由是否真正发生了变化
      const currentPath = route.path;
      const isRouteChanged = this.lastRoutePath !== currentPath;
      this.lastRoutePath = currentPath;
      
      if (route.meta && route.meta.showAlertPanel) {
        // 延迟执行，确保ShipMapView组件已完全加载
        this.$nextTick(() => {
          const shipMapView = this.$refs.shipMapView;
          if (shipMapView) {
            // 如果路由发生了变化，或者面板未显示，则显示面板
            if (isRouteChanged || !shipMapView.alertDetailVisible) {
              shipMapView.alertDetailVisible = true;
              shipMapView.alertActiveTab = 'records';
            }
          }
        });
      }
      
      if (route.meta && route.meta.showTrafficPanel) {
        // 延迟执行，确保ShipMapView组件已完全加载
        this.$nextTick(() => {
          console.log('showTrafficPanel@nextTick', route.meta.showTrafficPanel, 'isRouteChanged:', isRouteChanged)

          const shipMapView = this.$refs.shipMapView;
          if (shipMapView) {
            // 如果路由发生了变化，或者面板未显示，则显示面板
            if (isRouteChanged || !shipMapView.trafficPanelVisible) {
              shipMapView.trafficPanelVisible = true;
            }
          }
        });
      }
      
      if (route.meta && route.meta.showDeviationStatistics) {
        // 延迟执行，确保ShipMapView组件已完全加载
        this.$nextTick(() => {
          console.log('showDeviationStatistics@nextTick', route.meta.showDeviationStatistics, 'isRouteChanged:', isRouteChanged)

          const shipMapView = this.$refs.shipMapView;
          if (shipMapView) {
            // 如果路由发生了变化，或者面板未显示，则显示面板
            if (isRouteChanged || !shipMapView.deviationStatisticsVisible) {
              shipMapView.deviationStatisticsVisible = true;
              // 设置活跃标签页
              if (route.meta.statisticsActiveTab) {
                shipMapView.statisticsActiveTab = route.meta.statisticsActiveTab;
              }
            }
          }
        });
      }
    }
  }
}
</script>

<style scoped>
.ship-monitor-container {
  width: 100%;
  height: 100%;
  position: relative;
}
</style> 